import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Product } from "@/mocks/products";

interface Props {
  product: Product;
}

export default function ProductCard({ product }: Props) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const formatPrice = (price: number) =>
    `NT$ ${price.toLocaleString("zh-TW")}`;

  const categoryLabel: Record<string, string> = {
    dzi: "西藏天珠",
    crystal: "天然水晶礦石",
    jade: "翡翠玉石",
    thangka: "唐卡佛牌",
  };

  const categoryColor: Record<string, string> = {
    dzi: "#8b6f47",
    crystal: "#7a6a9a",
    jade: "#4a7a52",
    thangka: "#c9a96e",
  };

  return (
    <div
      className="group cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(`/products/${product.id}`)}
    >
      {/* Image */}
      <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-[#f5f0e8] mb-5">
        <img
          src={product.img}
          alt={product.name}
          className={`w-full h-full object-cover object-top transition-transform duration-700 ${
            hovered ? "scale-108" : "scale-100"
          }`}
          style={{ transform: hovered ? "scale(1.08)" : "scale(1)" }}
        />

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {product.badge && (
            <span
              className="px-3 py-1 bg-[#8b6f47] text-white text-[0.875rem] rounded-full font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              {product.badge}
            </span>
          )}
          {product.tag && (
            <span
              className="px-3 py-1 bg-[#1e1a16]/75 backdrop-blur-sm text-white text-[0.875rem] rounded-full font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              {product.tag}
            </span>
          )}
        </div>

        {/* Hover Overlay */}
        <div
          className={`absolute inset-0 bg-[#1e1a16]/50 flex items-center justify-center transition-opacity duration-300 ${
            hovered ? "opacity-100" : "opacity-0"
          }`}
        >
          <button
            className="px-7 py-3 bg-[#c9a96e] text-[#1e1a16] rounded-full text-[1rem] font-semibold tracking-wide whitespace-nowrap cursor-pointer hover:bg-[#b8945a] transition-colors"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
            onClick={(e) => { e.stopPropagation(); navigate(`/products/${product.id}`); }}
          >
            查看商品
          </button>
        </div>
      </div>

      {/* Info */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-[1rem] font-medium"
            style={{
              color: categoryColor[product.category],
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            {product.subcategory}
          </span>
        </div>
        <h3
          className="text-[#2d2520] font-bold mb-1.5 leading-[1.4]"
          style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
            fontFamily: "'Noto Serif TC', serif",
          }}
        >
          {product.name}
        </h3>
        <p
          className="text-[#7a6a5a] mb-4 leading-[1.6]"
          style={{
            fontSize: "1.1875rem",
            fontFamily: "'Noto Serif TC', serif",
          }}
        >
          {product.desc}
        </p>
        <div className="flex items-center justify-between">
          <span
            className="text-[#2d2520] font-bold"
            style={{
              fontSize: "1.25rem",
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            {formatPrice(product.price)}
          </span>
          <button
            className="px-5 py-2.5 bg-[#2d2520] text-white rounded-full text-[1rem] font-medium cursor-pointer hover:bg-[#3d3530] transition-all duration-300 whitespace-nowrap"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
            onClick={(e) => { e.stopPropagation(); navigate(`/products/${product.id}`); }}
          >
            立即選購
          </button>
        </div>
      </div>
    </div>
  );
}
